import { ArrowRightOutlined } from '@ant-design/icons';
import type { IdeaStatusLogEntry } from '@cult/schemas';
import { Skeleton, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import { BlankState, InlineError, UserAvatar } from '../../../../components';

import { useIdeaHistory } from '../../hooks';
import { IdeaStatusTag } from '../IdeaStatusTag/IdeaStatusTag';
import styles from './IdeaStatusHistory.module.scss';

const { Text } = Typography;

type Props = {
  ideaId: string;
  enabled: boolean;
};

export const IdeaStatusHistory = ({ ideaId, enabled }: Props) => {
  const query = useIdeaHistory(ideaId, { enabled });

  if (!enabled || query.isPending) {
    return <Skeleton active paragraph={{ rows: 3 }} />;
  }
  if (query.isError) {
    return <InlineError title="Couldn't load history" detail={query.error.message} />;
  }
  const entries = query.data?.entries ?? [];

  if (entries.length === 0) {
    return (
      <BlankState
        title="No transitions yet"
        hint="Status changes will appear here once this idea moves through the workflow."
      />
    );
  }

  return (
    <ol className={styles.list}>
      {entries.map((entry, i) => (
        <HistoryRow key={entry.id} entry={entry} index={i} isLast={i === entries.length - 1} />
      ))}
    </ol>
  );
};

const HistoryRow = ({
  entry,
  index,
  isLast,
}: {
  entry: IdeaStatusLogEntry;
  index: number;
  isLast: boolean;
}) => {
  return (
    <li className={styles.row} style={{ animationDelay: `${Math.min(index * 40, 200)}ms` }}>
      <div className={styles.rail} aria-hidden>
        <UserAvatar size={28} avatarUrl={entry.changedBy.avatarUrl} />
        {isLast ? null : <span className={styles.line} />}
      </div>
      <div className={styles.body}>
        <div className={styles.head}>
          <div className={styles.who}>
            <Text style={{ fontSize: 13, fontWeight: 500 }}>{entry.changedBy.displayName}</Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ·
            </Text>
            <Tooltip title={dayjs(entry.createdAt).format('MMM D, YYYY h:mm A')}>
              <Text type="secondary" style={{ fontSize: 12, cursor: 'default' }}>
                {dayjs(entry.createdAt).fromNow()}
              </Text>
            </Tooltip>
          </div>
          <div className={styles.transition}>
            {entry.fromStatus ? (
              <>
                <IdeaStatusTag status={entry.fromStatus} />
                <ArrowRightOutlined className={styles.arrow} />
                <IdeaStatusTag status={entry.toStatus} />
              </>
            ) : (
              <IdeaStatusTag status={entry.toStatus} />
            )}
          </div>
        </div>
        {entry.note ? <div className={styles.note}>{entry.note}</div> : null}
      </div>
    </li>
  );
};
